import { useQueries, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { FocusEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { getLineItemsApi } from 'api/repairOrder';
import { getTicketRequestSubCategoriesByCategoryId } from 'api/ticketRequestSubCategories';
import { getAdvisorNumberListApi, getTechNumberListApi } from 'api/tickets';
import { useTickets } from 'contexts/TicketsContext';
import { useUserProfile } from 'contexts/UserProfileContext';
import type { CreateTicketRequestBodyDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { LineItemI } from 'declarations/interfaces/lineItems';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import type { TicketRequestSubcategoryI } from 'declarations/interfaces/ticketRequestSubcategories';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { parseRequestCategoryId } from 'helpers/categoryAllowsAttachments';
import { getOemFamilyIdByRoofTopId } from 'helpers/getOemFamilyByRoofTop';
import { parseCreatedTicketId } from 'helpers/parseCreatedTicketId';
import { convertNumbersToSelectOptions } from 'helpers/selectOptions';
import { uploadTicketAttachmentsFromFiles } from 'helpers/uploadTicketAttachmentsFromFiles';
import { useGetAttachmentTypesApi } from 'hooks/api/useAttachments';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchAgentsList } from 'hooks/api/useTicketAgentsList';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRoExists } from 'hooks/api/useTicketRoNumber';
import { useCreateTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import { buildCombinedCategorySubcategoryOptions } from '../categorySubcategoryOptions';
import type { NewTicketFormValuesT } from '../declarations';
import { hasCreationAttachmentFiles } from '../helpers/creationAttachmentFormValues';

type UseOpenNewTicketPopupParamsT = {
  categoryRows?: string[];
  isMultipleTickets?: boolean;
  bulkCategoryIds?: number[];
};

type UseOpenNewTicketPopupT = {
  requestCategoriesOptions: SelectOptionT[];
  isLoadingRequestCategoriesOptions: boolean;
  combinedCategorySubcategoryOptions: SelectOptionT[];
  isLoadingCombinedCategorySubcategoryOptions: boolean;
  subcategoriesByCategoryId: Record<number, TicketRequestSubcategoryI[]>;
  claimStatusesOptions: SelectOptionT[];
  isLoadingClaimStatusesOptions: boolean;
  ticketPrioritiesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingRoofTopOptions: boolean;
  isLoadingAgentsOptions: boolean;
  roofTopIdsOptions: SelectOptionT[];
  agentIdsOptions: SelectOptionT[];
  isPopupOpened: boolean;
  handleSubmitForm: (data: NewTicketFormValuesT) => Promise<void>;
  handleAmountBlur: (
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  isCreatingNewTicket: boolean;
  handleCloseNewTicketPopup: NoopT;
  handleRoofTopChange: (roofTopId: string) => void;
  handleAgentChange: (agentId: string) => void;
  ticketRequestCategories: TicketRequestCategoryI[] | undefined;
  isRoWarningPopupOpen: boolean;
  advisorNumber: string | undefined;
  checkIfRoExists: (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
  handleLineNumberChange: (lineNumber: string) => void;
  handleROChange: (roNumber: string) => void;
  lineItemsObj: LineItemI[];
  lineNumbers: SelectOptionT[];
  advisorNumbers: SelectOptionT[];
  techNumbers: SelectOptionT[];
  recentRoData: { roofTopId: string | null; roNumber: string | null };
  setAdvisorNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsRoWarningPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLineItemsObj: React.Dispatch<React.SetStateAction<LineItemI[]>>;
  setLineNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setAdvisorNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setTechNumbers: React.Dispatch<React.SetStateAction<SelectOptionT[]>>;
  setRecentRoData: React.Dispatch<
    React.SetStateAction<{
      roofTopId: string | null;
      roNumber: string | null;
      agentId: string | null;
    }>
  >;
  setShowLineNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowTechNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAdvisorNumberDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLineNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  showLineNumberDropdown: boolean;
  showTechNumberDropdown: boolean;
  showAdvisorNumberDropdown: boolean;
  setSelectedRoofTopId: React.Dispatch<React.SetStateAction<string | undefined>>;
  getOemFamilyIdForRoofTop: (roofTopId: string | undefined) => string | undefined;
  handleAdvisorNumberChange: (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ) => void;
  creationAttachmentTypesOptions: SelectOptionT[];
  isLoadingCreationAttachmentTypesOptions: boolean;
};

/** Matches assign-to-WarrCloud default on ticket details when sending to the opposite party. */
const assignToOppositeUserOnCreationAttachment = true;

const defaultCategoryRows: string[] = [];

const useOpenNewTicketPopup = (params?: UseOpenNewTicketPopupParamsT): UseOpenNewTicketPopupT => {
  const categoryRows = params?.categoryRows ?? defaultCategoryRows;
  /**
   * Hooks
   */
  const { setNewTicketPopupVisibility, isNewTicketPopupOpened, scheduleCategoryId, roofTopsList } =
    useTickets();
  const [selectedRoofTopId, setSelectedRoofTopId] = useState<string | undefined>(undefined);
  const [, setSelectedAgentId] = useState<string | undefined>(undefined);
  const { userProfile, isLoadingUserProfile } = useUserProfile();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [, setSelectedRoNumber] = useState<string | null>(null);
  const [, setSelectedLineNumber] = useState<string | undefined>(undefined);
  const [isRoWarningPopupOpen, setIsRoWarningPopupOpen] = useState(false);
  const [showLineNumberDropdown, setShowLineNumberDropdown] = useState(false);
  const [showTechNumberDropdown, setShowTechNumberDropdown] = useState(false);
  const [showAdvisorNumberDropdown, setShowAdvisorNumberDropdown] = useState(false);
  const [advisorNumber, setAdvisorNumber] = useState<string | undefined>(undefined);
  const [lineNumbers, setLineNumbers] = useState<SelectOptionT[]>([]);
  const [techNumbers, setTechNumbers] = useState<SelectOptionT[]>([]);
  const [advisorNumbers, setAdvisorNumbers] = useState<SelectOptionT[]>([]);
  const [lineItemsObj, setLineItemsObj] = useState<LineItemI[]>([]);
  const [recentRoData, setRecentRoData] = useState<{
    roofTopId: string | null;
    roNumber: string | null;
    agentId: string | null;
  }>({
    roofTopId: null,
    roNumber: null,
    agentId: null,
  });
  const [isPostingCreationAttachments, setIsPostingCreationAttachments] = useState(false);
  /**
   * Api hooks
   */
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    isNewTicketPopupOpened,
    selectedRoofTopId
  );

  const [searchParams] = useSearchParams();
  const effectiveRoofTopId =
    searchParams.get(TicketsRefinementKeysE.RoofTopId) || selectedRoofTopId || '';
  const getOemFamilyIdForRoofTop = useCallback(
    (roofTopId: string | undefined): string | undefined =>
      getOemFamilyIdByRoofTopId(roofTopId, roofTopsList),
    [roofTopsList]
  );
  /** Use selectedRoofTopId (dealer chosen in modal) for OEM—not URL dealer—so subcategories match the dealer's OEM. */
  const oemFamilyIdForSubcategories = useMemo(
    (): string | undefined => getOemFamilyIdByRoofTopId(selectedRoofTopId, roofTopsList),
    [selectedRoofTopId, roofTopsList]
  );

  const ticketRequestCategoriesForPopup = useMemo(
    (): TicketRequestCategoryI[] =>
      ticketRequestCategoriesResult.data?.filter(category => category.id !== scheduleCategoryId) ??
      [],
    [ticketRequestCategoriesResult.data, scheduleCategoryId]
  );

  const categoryIdsForSubcategories = useMemo(
    (): number[] => ticketRequestCategoriesForPopup.map(({ id }) => id),
    [ticketRequestCategoriesForPopup]
  );

  const subcategoryQueriesByCategory = useQueries({
    queries: categoryIdsForSubcategories.map(
      (
        categoryId
      ): {
        queryKey: unknown[];
        queryFn: () => Promise<TicketRequestSubcategoryI[]>;
        enabled: boolean;
        staleTime: number;
      } => ({
        queryKey: [
          RequestQueryKeysE.FetchTicketRequestSubcategories,
          'byCategoryId',
          categoryId,
          oemFamilyIdForSubcategories,
        ],
        queryFn: (): Promise<TicketRequestSubcategoryI[]> =>
          getTicketRequestSubCategoriesByCategoryId(categoryId, oemFamilyIdForSubcategories),
        enabled:
          isNewTicketPopupOpened && !!selectedRoofTopId && categoryIdsForSubcategories.length > 0,
        staleTime: RequestStaleTimeE.TicketRequestCategories,
      })
    ),
  });

  const subcategoriesByCategoryId = useMemo((): Record<number, TicketRequestSubcategoryI[]> => {
    const map: Record<number, TicketRequestSubcategoryI[]> = {};
    categoryIdsForSubcategories.forEach((categoryId, index): void => {
      const data = subcategoryQueriesByCategory[index]?.data;
      if (data) {
        map[categoryId] = data;
      }
    });
    return map;
  }, [categoryIdsForSubcategories, subcategoryQueriesByCategory]);

  const combinedCategorySubcategoryOptions = useMemo(
    (): SelectOptionT[] =>
      buildCombinedCategorySubcategoryOptions(
        ticketRequestCategoriesForPopup,
        subcategoriesByCategoryId
      ),
    [ticketRequestCategoriesForPopup, subcategoriesByCategoryId]
  );

  const isLoadingCombinedCategorySubcategoryOptions = subcategoryQueriesByCategory.some(
    (query): boolean => query.isLoading
  );

  const roofTopId = effectiveRoofTopId;

  const claimStatusesRequestParams = {
    isEnabled: isNewTicketPopupOpened,
    roofTopId: roofTopId,
  };
  const ticketUseFetchAgentsListResult = useFetchAgentsList(isNewTicketPopupOpened, [
    selectedRoofTopId as string,
  ]);
  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);
  const ticketPrioritiesResult = useFetchTicketPriorities(isNewTicketPopupOpened);
  const attachmentTypesResult = useGetAttachmentTypesApi();
  const createTicketMutation = useCreateTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });
  const roQuery = useFetchTicketRoExists(
    recentRoData.roofTopId as string,
    recentRoData.roNumber as string
  );
  /**
   * Variables
   */
  const requestCategoriesOptions = useMemo(
    () =>
      ticketRequestCategoriesResult.data
        ? ticketRequestCategoriesResult.data
            ?.filter(category => category.id !== scheduleCategoryId)
            .map(({ id, categoryName }) => ({
              label: categoryName,
              value: id,
            }))
        : [],
    [ticketRequestCategoriesResult.data, scheduleCategoryId]
  );
  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  const roofTopIdsOptions = useMemo(
    () =>
      userProfile
        ? userProfile?.rooftopPermissions
            .map(({ rooftopId, rooftop }) => ({ label: rooftop, value: rooftopId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [userProfile]
  );

  const agentIdsOptions = useMemo(
    () =>
      ticketUseFetchAgentsListResult.data
        ? ticketUseFetchAgentsListResult.data
            .map(({ userId, displayName }) => ({ label: displayName, value: userId }))
            .sort((a, b) => {
              if (a.label > b.label) {
                return 1;
              }

              if (a.label < b.label) {
                return -1;
              }

              return 0;
            })
        : [],
    [ticketUseFetchAgentsListResult.data]
  );

  const creationAttachmentTypesOptions = useMemo(
    () =>
      attachmentTypesResult.data
        ? attachmentTypesResult.data.map(({ attachmentTypeCode, attachmentTypeDescription }) => ({
            label: attachmentTypeDescription,
            value: attachmentTypeCode,
          }))
        : [],
    [attachmentTypesResult.data]
  );

  /**
   * Handlers
   */
  const resetTicketMutationState = useCallback((): void => {
    if (createTicketMutation.isSuccess || createTicketMutation.isError) {
      createTicketMutation.reset();
    }
  }, [createTicketMutation]);

  const handleCloseNewTicketPopup = useCallback((): void => {
    setNewTicketPopupVisibility(false);
    resetTicketMutationState();
  }, [resetTicketMutationState, setNewTicketPopupVisibility]);

  const setAmountValue = (value: string, setValue: UseFormSetValue<NewTicketFormValuesT>): void => {
    setValue('amount', value, { shouldValidate: true });
  };
  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<NewTicketFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );
  const handleSubmitForm = useCallback(
    async (data: NewTicketFormValuesT): Promise<void> => {
      const onMutationSuccess = (hasAttachments: boolean, createdMultiple: boolean): void => {
        let snackbarMessage = 'Ticket successfully created';
        if (hasAttachments && createdMultiple) {
          snackbarMessage = 'Tickets and attachments successfully created';
        } else if (hasAttachments) {
          snackbarMessage = 'Ticket and attachments successfully created';
        } else if (createdMultiple) {
          snackbarMessage = 'Tickets successfully created';
        }
        dispatch(
          updateSnackbarNotification({
            snackbarMessage,
            snackBarSeverity: 'success',
          })
        );
        handleCloseNewTicketPopup();
      };
      const onMutationError = (error: unknown): void => {
        const errorMsg =
          getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
        dispatch(
          updateSnackbarNotification({
            snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
            snackBarSeverity: 'error',
          })
        );
        handleCloseNewTicketPopup();
      };

      if (data.createMultipleTickets) {
        setIsPostingCreationAttachments(true);
        try {
          let attachmentsUploaded = false;
          let ticketsCreated = 0;
          let firstTicketId: string | undefined;
          const sharedHasCreationAttachments = hasCreationAttachmentFiles(data);
          for (const rowId of categoryRows) {
            const categoryKey = `requestCategoryId-${rowId}` as keyof NewTicketFormValuesT;
            const categoryVal = data[categoryKey];
            if (categoryVal === undefined || categoryVal === null) {
              continue;
            }
            const categoryIdNum = categoryVal as number;
            const textKey = `text-${rowId}` as keyof NewTicketFormValuesT;
            const subcategoryKey = `requestSubcategoryId-${rowId}` as keyof NewTicketFormValuesT;
            const subcategoryNameKey =
              `requestSubCategoryName-${rowId}` as keyof NewTicketFormValuesT;
            const subcategoryIdValue = data[subcategoryKey] as number | null | undefined;
            const requestSubCategoryName =
              (data[subcategoryNameKey] as string | null | undefined) || null;

            const parsedRow: CreateTicketRequestBodyDTO = {
              requestCategoryId: categoryIdNum,
              requestSubCategoryId: subcategoryIdValue ?? null,
              requestSubCategoryName: requestSubCategoryName,
              roDate: data.roDate ? format(data.roDate, 'yyyy-MM-dd') : '1970-01-01',
              amount: Number(data.amount),
              text: (data[textKey] as string | undefined) ?? '',
              roofTopId: data.roofTopId,
              agentId: data.agentId,
              roNumber: data.roNumber,
              lineNumber: data.lineNumber,
              techNumber: data.techNumber,
              advisorNumber: data.advisorNumber,
              vin: data.vin ?? undefined,
              claimStatusId: data.claimStatusId,
              ticketPriorityId: data.ticketPriorityId,
              claimSubmittedDate: data.claimSubmittedDate
                ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
                : null,
            };

            const createResult = await createTicketMutation.mutateAsync(parsedRow);
            ticketsCreated += 1;

            if (!firstTicketId) {
              firstTicketId = parseCreatedTicketId(createResult);
            }
          }

          if (sharedHasCreationAttachments && firstTicketId) {
            try {
              const firstCategoryId = parseRequestCategoryId(
                data[`requestCategoryId-${categoryRows[0]}` as keyof NewTicketFormValuesT]
              );
              const categoryName =
                ticketRequestCategoriesResult.data?.find(c => Number(c.id) === firstCategoryId)
                  ?.categoryName ?? '';
              const notesTrimmed = String(data.creationAttachmentNote ?? '').trim();
              const notes = notesTrimmed || categoryName;

              await uploadTicketAttachmentsFromFiles({
                ticketId: firstTicketId,
                attachmentTypeCode: String(data.creationAttachmentTypeCode ?? ''),
                notes,
                assignToOppositeUser: assignToOppositeUserOnCreationAttachment,
                attachmentFileList: data.creationAttachmentFile,
                attachmentPhotoList: data.creationAttachmentPhoto,
              });
              attachmentsUploaded = true;
            } catch (attachError) {
              const errorMsg =
                getErrorMessage(attachError as ApiErrorI) ||
                getValidationErrorMessage(attachError as ApiErrorI);
              dispatch(
                updateSnackbarNotification({
                  snackbarMessage: `Tickets were created, but uploading attachments failed. ${
                    errorMsg || ''
                  }`,
                  snackBarSeverity: 'error',
                })
              );
              handleCloseNewTicketPopup();
              return;
            }
          } else if (sharedHasCreationAttachments && !firstTicketId) {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage:
                  'Tickets were created, but attachments could not be uploaded because the server did not return a ticket id. Add attachments from a ticket.',
                snackBarSeverity: 'warning',
              })
            );
          }

          if (ticketsCreated === 0) {
            onMutationError(new Error('No tickets to create'));
            return;
          }

          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
          queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);

          onMutationSuccess(attachmentsUploaded, ticketsCreated > 1);
        } catch (error) {
          onMutationError(error);
        } finally {
          setIsPostingCreationAttachments(false);
        }
      } else {
        const parsedData = {
          requestCategoryId: data.requestCategoryId,
          requestSubCategoryId: data.requestSubcategoryId || null,
          requestSubCategoryName: data.requestSubCategoryName || null,
          roDate: data.roDate ? format(data.roDate, 'yyyy-MM-dd') : '1970-01-01',
          amount: Number(data.amount),
          text: data.text,
          roofTopId: data.roofTopId,
          agentId: data.agentId,
          roNumber: data.roNumber,
          lineNumber: data.lineNumber,
          techNumber: data.techNumber,
          advisorNumber: data.advisorNumber,
          vin: data.vin ?? undefined,
          claimStatusId: data.claimStatusId,
          ticketPriorityId: data.ticketPriorityId,
          claimSubmittedDate: data.claimSubmittedDate
            ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
            : null,
          subCategoryId: data.subCategoryId || null,
        };
        const hasCreationAttachments = hasCreationAttachmentFiles(data);

        setIsPostingCreationAttachments(true);
        try {
          const createResult = await createTicketMutation.mutateAsync(parsedData);
          let attachmentsUploaded = false;

          if (hasCreationAttachments) {
            const ticketId = parseCreatedTicketId(createResult);
            if (!ticketId) {
              dispatch(
                updateSnackbarNotification({
                  snackbarMessage:
                    'Ticket was created, but attachments could not be uploaded because the server did not return a ticket id. Add attachments from the ticket.',
                  snackBarSeverity: 'warning',
                })
              );
            } else {
              try {
                const categoryName =
                  ticketRequestCategoriesResult.data?.find(
                    c => Number(c.id) === Number(data.requestCategoryId)
                  )?.categoryName ?? '';
                const notesTrimmed = data.creationAttachmentNote?.trim();
                const notes = notesTrimmed || categoryName;

                await uploadTicketAttachmentsFromFiles({
                  ticketId,
                  attachmentTypeCode: String(data.creationAttachmentTypeCode ?? ''),
                  notes,
                  assignToOppositeUser: assignToOppositeUserOnCreationAttachment,
                  attachmentFileList: data.creationAttachmentFile,
                  attachmentPhotoList: data.creationAttachmentPhoto,
                });

                attachmentsUploaded = true;

                queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
                queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
                queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
                queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
              } catch (attachError) {
                const errorMsg =
                  getErrorMessage(attachError as ApiErrorI) ||
                  getValidationErrorMessage(attachError as ApiErrorI);
                dispatch(
                  updateSnackbarNotification({
                    snackbarMessage: `Ticket was created, but uploading attachments failed. ${
                      errorMsg || ''
                    }`,
                    snackBarSeverity: 'error',
                  })
                );
                handleCloseNewTicketPopup();
                return;
              }
            }
          }

          onMutationSuccess(attachmentsUploaded, false);
        } catch (error) {
          onMutationError(error);
        } finally {
          setIsPostingCreationAttachments(false);
        }
      }
    },
    [
      categoryRows,
      createTicketMutation,
      dispatch,
      handleCloseNewTicketPopup,
      queryClient,
      ticketRequestCategoriesResult,
    ]
  );

  const handleRoofTopChange = useCallback((roofTopId: string): void => {
    setSelectedRoofTopId(roofTopId);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
    }));
  }, []);

  const handleAgentChange = (agentId: string): void => {
    setSelectedAgentId(agentId);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      agentId: agentId,
    }));
  };

  const handleROChange = (roNumber: string | null): void => {
    setSelectedRoNumber(roNumber);
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roNumber: roNumber,
    }));
  };

  const handleAdvisorNumberChange = (
    advisorNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): void => {
    setAdvisorNumber(advisorNumber);
    setValue('advisorNumber', advisorNumber);
  };

  const handleLineNumberChange = (lineNumber: string): void => {
    setSelectedLineNumber(recentRoData.roNumber ? lineNumber : '');
  };

  const setAdvisorLineNumber = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    setRecentRoData((prevRoData): typeof prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
      roNumber: roNumber,
    }));
    const roResult = roQuery.data;
    setValue('advisorNumber', roResult?.serviceAdvisorNumber ?? advisorNumber);
    setAdvisorNumber(roResult?.serviceAdvisorNumber ?? advisorNumber);
    if (roQuery.isError || (roNumber && roQuery.data === undefined)) {
      setShowLineNumberDropdown(false);
      setSelectedLineNumber(undefined);
      setValue('lineNumber', '');
      setLineItemsObj([]);
      let advisorOptions: SelectOptionT[] = [];
      let techOptions: SelectOptionT[] = [];
      try {
        const advisorNumbers = await getAdvisorNumberListApi(roofTopId);
        advisorOptions = convertNumbersToSelectOptions(advisorNumbers);
      } catch (e) {
        // If fetching advisor numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        advisorOptions = [];
      }

      try {
        const techNumbers = await getTechNumberListApi(roofTopId);
        techOptions = convertNumbersToSelectOptions(techNumbers);
      } catch (e) {
        // If fetching tech numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        techOptions = [];
      }

      const hasAdvisorOptions = advisorOptions.length > 0;
      const hasTechOptions = techOptions.length > 0;

      setAdvisorNumbers(advisorOptions);
      setTechNumbers(techOptions);
      setShowAdvisorNumberDropdown(hasAdvisorOptions);
      setShowTechNumberDropdown(hasTechOptions);
    } else {
      const lineItems = getLineItemsApi(roofTopId, roNumber);
      setLineItemsObj(await lineItems);
      setAdvisorNumbers([]);
      setTechNumbers([]);
      setShowAdvisorNumberDropdown(false);
      setShowTechNumberDropdown(false);

      const mappedLineItems = (await lineItems).map(item => {
        return {
          value: item.lineNumber,
          label: item.lineNumber,
        };
      });
      if (mappedLineItems.length > 0) {
        setLineNumbers(mappedLineItems);
        setShowLineNumberDropdown(true);
      } else {
        setShowLineNumberDropdown(false);
      }
    }
  };

  const checkIfRoExists = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<NewTicketFormValuesT>
  ): Promise<void> => {
    if (!roofTopId && !roNumber) {
      return;
    }
    if (roofTopId && roNumber && roNumber !== null) {
      setAdvisorLineNumber(roofTopId, roNumber, setValue);
    }
  };

  return {
    requestCategoriesOptions,
    isLoadingRequestCategoriesOptions:
      ticketRequestCategoriesResult.isLoading && !!selectedRoofTopId,
    combinedCategorySubcategoryOptions,
    isLoadingCombinedCategorySubcategoryOptions,
    subcategoriesByCategoryId,
    isLoadingAgentsOptions: ticketUseFetchAgentsListResult.isLoading && !!selectedRoofTopId,
    claimStatusesOptions,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    ticketPrioritiesOptions,
    isLoadingTicketPrioritiesOptions: claimStatusesResult.isLoading,
    isLoadingRoofTopOptions: isLoadingUserProfile,
    roofTopIdsOptions,
    agentIdsOptions,
    handleCloseNewTicketPopup,
    isPopupOpened: isNewTicketPopupOpened,
    handleSubmitForm,
    handleAmountBlur,
    isCreatingNewTicket: createTicketMutation.isLoading || isPostingCreationAttachments,
    handleRoofTopChange,
    handleAgentChange,
    ticketRequestCategories: ticketRequestCategoriesResult.data,
    advisorNumber,
    checkIfRoExists,
    handleLineNumberChange,
    handleROChange,
    lineItemsObj,
    lineNumbers,
    recentRoData,
    setAdvisorNumber,
    setIsRoWarningPopupOpen,
    setLineItemsObj,
    setLineNumbers,
    setRecentRoData,
    setSelectedLineNumber,
    setShowLineNumberDropdown,
    showLineNumberDropdown,
    isRoWarningPopupOpen,
    setSelectedRoofTopId,
    getOemFamilyIdForRoofTop,
    setShowAdvisorNumberDropdown,
    setShowTechNumberDropdown,
    showAdvisorNumberDropdown,
    showTechNumberDropdown,
    setAdvisorNumbers,
    setTechNumbers,
    techNumbers,
    advisorNumbers,
    handleAdvisorNumberChange,
    creationAttachmentTypesOptions,
    isLoadingCreationAttachmentTypesOptions: attachmentTypesResult.isLoading,
  };
};

export default useOpenNewTicketPopup;
